(() => {
  if (window.RexingCardRatings) {
    window.RexingCardRatings.initialize(document);
    return;
  }

  const initialized = new WeakSet();
  const bvCache = new Map();
  const judgeCache = new Map();
  const bvQueue = new Map();
  const requestQueue = [];
  let activeRequests = 0;
  let bvScheduled = false;
  const defaults = {
    loading: 'Loading product rating…',
    unavailable: 'Product rating unavailable',
    empty: 'No product ratings yet',
    label: '{rating} out of 5 based on {count} product ratings. Read product reviews.',
    count: '{count} ratings',
    average: '{rating} / 5'
  };

  const number = (value) => {
    if (value === null || value === undefined || value === '') return null;
    const result = Number(value);
    return Number.isFinite(result) ? result : null;
  };

  const count = (value) => {
    const result = number(value);
    return result !== null && result >= 0 && Number.isInteger(result) ? result : null;
  };

  const ratingSummary = (totalValue, averageValue, distribution = []) => {
    const total = count(totalValue);
    if (total === null) return null;
    if (total === 0) return { count: 0, sum: 0 };
    let histogramCount = 0;
    let histogramSum = 0;
    let valid = Array.isArray(distribution) && distribution.length > 0;
    const seen = new Set();
    (Array.isArray(distribution) ? distribution : []).forEach((row) => {
      const rating = number(row.rating ?? row.RatingValue);
      const frequency = count(row.count ?? row.Count);
      if (rating === null || rating < 1 || rating > 5 || !Number.isInteger(rating) || frequency === null || seen.has(rating)) {
        valid = false;
        return;
      }
      seen.add(rating);
      histogramCount += frequency;
      histogramSum += rating * frequency;
    });
    if (valid && histogramCount === total) return { count: total, sum: histogramSum };
    const average = number(averageValue);
    return average !== null && average >= 1 && average <= 5 ? { count: total, sum: average * total } : null;
  };

  const combine = (summaries) => {
    if (!summaries.every((summary) => summary !== null)) return null;
    const total = summaries.reduce((sum, summary) => sum + summary.count, 0);
    return { count: total, average: total ? summaries.reduce((sum, summary) => sum + summary.sum, 0) / total : null };
  };

  const runRequests = () => {
    while (activeRequests < 4 && requestQueue.length) {
      const { url, resolve, reject } = requestQueue.shift();
      activeRequests += 1;
      const controller = new AbortController();
      const timer = window.setTimeout(() => controller.abort(), 15000);
      fetch(url, { method: 'GET', credentials: 'omit', mode: 'cors', headers: { Accept: 'application/json' }, signal: controller.signal })
        .then((response) => {
          if (!response.ok) throw new Error('Rating request failed');
          return response.json();
        })
        .then(resolve, reject)
        .finally(() => {
          window.clearTimeout(timer);
          activeRequests -= 1;
          runRequests();
        });
    }
  };

  const request = (url) => new Promise((resolve, reject) => {
    requestQueue.push({ url, resolve, reject });
    runRequests();
  });

  const flushBV = () => {
    bvScheduled = false;
    const groups = Array.from(bvQueue.entries());
    bvQueue.clear();
    groups.forEach(([passkey, products]) => {
      const entries = Array.from(products.entries());
      for (let offset = 0; offset < entries.length; offset += 50) {
        const batch = entries.slice(offset, offset + 50);
        const url = new URL('https://api.bazaarvoice.com/data/products.json');
        url.search = new URLSearchParams({ apiversion: '5.4', passkey, Filter: `Id:${batch.map(([id]) => id).join(',')}`, Stats: 'Reviews', Limit: '100' });
        request(url.href).then((payload) => {
          if (payload.HasErrors || !Array.isArray(payload.Results)) throw new Error('Invalid rating response');
          const summaries = new Map();
          payload.Results.forEach((row) => {
            const statistics = row.ReviewStatistics;
            if (!statistics) return;
            summaries.set(String(row.Id), ratingSummary(statistics.TotalReviewCount, statistics.AverageOverallRating, statistics.RatingDistribution));
          });
          batch.forEach(([id, pending]) => {
            const summary = summaries.get(id);
            if (!summary) pending.reject(new Error('Missing product rating'));
            else pending.resolve(summary);
          });
        }).catch((error) => batch.forEach(([, pending]) => pending.reject(error)));
      }
    });
  };

  const getBVSummary = (productId, passkey) => {
    if (!passkey || !/^\d+$/.test(productId)) return Promise.reject(new Error('Missing rating configuration'));
    const cacheKey = `${passkey}:${productId}`;
    if (bvCache.has(cacheKey)) return bvCache.get(cacheKey);
    const promise = new Promise((resolve, reject) => {
      if (!bvQueue.has(passkey)) bvQueue.set(passkey, new Map());
      bvQueue.get(passkey).set(productId, { resolve, reject });
      if (!bvScheduled) {
        bvScheduled = true;
        queueMicrotask(flushBV);
      }
    }).catch((error) => {
      bvCache.delete(cacheKey);
      throw error;
    });
    bvCache.set(cacheKey, promise);
    return promise;
  };

  const getJudgeSummary = (node) => {
    const productId = node.dataset.productId;
    const shop = node.dataset.judgeShop;
    const cacheKey = `${shop}:${productId}`;
    try {
      const payload = JSON.parse(node.dataset.judgeSummary || '{}');
      const summary = ratingSummary(payload.count, payload.average, payload.distribution);
      if (summary) {
        const promise = Promise.resolve(summary);
        judgeCache.set(cacheKey, promise);
        return promise;
      }
    } catch (error) {
      judgeCache.delete(cacheKey);
    }
    if (judgeCache.has(cacheKey)) return judgeCache.get(cacheKey);
    if (!shop || !/^\d+$/.test(productId)) return Promise.reject(new Error('Missing rating configuration'));
    const url = new URL('https://api.judge.me/reviews/reviews_for_widget');
    url.search = new URLSearchParams({ shop_domain: shop, platform: 'shopify', product_id: productId, page: '1', per_page: '1', sort_by: 'created_at', sort_dir: 'desc' });
    const promise = request(url.href).then((payload) => {
      if (typeof payload.html !== 'string' || count(payload.page) !== 1 || count(payload.total_count) !== 0) throw new Error('Product rating unavailable');
      return { count: 0, sum: 0 };
    }).catch((error) => {
      judgeCache.delete(cacheKey);
      throw error;
    });
    judgeCache.set(cacheKey, promise);
    return promise;
  };

  const element = (tag, className, text) => {
    const node = document.createElement(tag);
    node.className = className;
    if (text !== undefined) node.textContent = text;
    return node;
  };

  const render = (node, result, copy) => {
    if (!node.isConnected) return;
    node.dataset.rrhRatingState = result.count ? 'ready' : 'empty';
    node.dataset.rrhRatingCount = String(result.count);
    if (result.average === null) {
      delete node.dataset.rrhRatingAverage;
      node.textContent = copy('empty');
      return;
    }
    node.dataset.rrhRatingAverage = String(result.average);
    const rating = result.average.toFixed(1);
    const link = element('a', 'rrh-card-rating__link');
    const destination = new URL(node.dataset.productUrl, window.location.href);
    if (destination.origin !== window.location.origin) throw new Error('Invalid product URL');
    destination.hash = 'rexing-reviews';
    link.href = destination.href;
    link.setAttribute('aria-label', copy('label', { rating, count: result.count }));
    const stars = element('span', 'rrh-card-rating__stars');
    stars.setAttribute('aria-hidden', 'true');
    stars.append(element('span', 'rrh-card-rating__track', '★★★★★'));
    const fill = element('span', 'rrh-card-rating__fill', '★★★★★');
    fill.style.width = `${Math.max(0, Math.min(100, result.average / 5 * 100))}%`;
    stars.append(fill);
    link.append(stars, element('span', 'rrh-card-rating__average', copy('average', { rating })), element('span', 'rrh-card-rating__count', copy('count', { count: result.count })));
    node.replaceChildren(link);
  };

  const initialize = (scope = document) => {
    const nodes = Array.from(scope.querySelectorAll?.('[data-rrh-card-rating]') || []);
    if (scope.matches?.('[data-rrh-card-rating]')) nodes.unshift(scope);
    nodes.forEach((node) => {
      if (initialized.has(node)) return;
      initialized.add(node);
      let labels = { ...defaults };
      try {
        labels = { ...labels, ...JSON.parse(node.dataset.i18n || '{}') };
      } catch (error) {
        labels = { ...defaults };
      }
      const copy = (key, values = {}) => String(labels[key] || defaults[key]).replace(/\{(\w+)\}/g, (match, name) => values[name] === undefined ? match : String(values[name]));
      Promise.all([getBVSummary(node.dataset.productId, node.dataset.bvPasskey), getJudgeSummary(node)])
        .then((summaries) => {
          const result = combine(summaries);
          if (!result) throw new Error('Product rating unavailable');
          render(node, result, copy);
        })
        .catch(() => {
          if (!node.isConnected) return;
          node.dataset.rrhRatingState = 'unavailable';
          delete node.dataset.rrhRatingAverage;
          delete node.dataset.rrhRatingCount;
          node.textContent = copy('unavailable');
        });
    });
  };

  const scrollToReviews = () => {
    if (window.location.hash !== '#rexing-reviews') return;
    const hub = document.querySelector('[data-rexing-reviews-hub]');
    if (hub) window.requestAnimationFrame(() => hub.scrollIntoView({ block: 'start' }));
  };

  const start = () => {
    initialize(document);
    const observer = new MutationObserver((records) => {
      records.forEach((record) => record.addedNodes.forEach((node) => {
        if (node.nodeType === 1) initialize(node);
      }));
    });
    observer.observe(document.body, { childList: true, subtree: true });
    scrollToReviews();
  };

  window.RexingCardRatings = { initialize };
  document.addEventListener('shopify:section:load', (event) => initialize(event.target));
  window.addEventListener('hashchange', scrollToReviews);
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', start, { once: true });
  else start();
})();
